import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { format, startOfWeek, addDays, isSameDay } from 'date-fns'
import { fr } from 'date-fns/locale'
import { generateFacturePDF } from '../lib/generateFacturePDF'
import SchemaCorporel from './SchemaCorporel'

const COULEURS = [
  { bg: '#E6F1FB', border: '#378ADD', text: '#0C447C' },
  { bg: '#EAF3DE', border: '#639922', text: '#27500A' },
]
// [AGENDA-COULEURS v1] couleur fixe par praticienne (fiable même si la liste n'est pas encore chargée)
const COULEUR_PRATICIEN = {
  '3b5fa0a5-002c-47db-9a99-8e5bd51d8c8b': 0, // Vanessa Whitelaw — bleu
  '7c9861fa-7a3d-4d6d-9b24-1997791ae847': 1, // Céline Eckart — vert
}
const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']
const HEURE_DEBUT = 7
const HEURE_FIN = 20
const PX_PAR_HEURE = 80

function getAge(dob) {
  if (!dob) return null
  const today = new Date()
  const d = new Date(dob)
  let age = today.getFullYear() - d.getFullYear()
  const m = today.getMonth() - d.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--
  return age
}

function profilPatient(patient) {
  if (!patient) return null
  if (patient.enceinte) return { symbole: '🤰', label: 'Enceinte' }
  const age = getAge(patient.date_naissance)
  if (age === null) return null
  if (age < 12) return { symbole: '🧒', label: age + ' ans' }
  if (age < 18) return { symbole: '🧑', label: age + ' ans' }
  if (age >= 75) return { symbole: '👴', label: age + ' ans' }
  return null
}

function timeToMinutes(t) {
  if (!t) return 0
  const [h, m] = t.split(':').map(Number)
  return h * 60 + m
}

function minutesToPx(min) {
  return ((min - HEURE_DEBUT * 60) / 60) * PX_PAR_HEURE
}

function minutesToTime(min) {
  const h = Math.floor(min / 60)
  const m = min % 60
  return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0')
}

function creneauxLibres(hDebut, hFin, duree, rdvsExistants, praticienId) {
  const debut = timeToMinutes(hDebut)
  const fin = timeToMinutes(hFin)
  const creneaux = []
  for (let t = debut; t + duree <= fin; t += duree) {
    const occupe = rdvsExistants.some(r => {
      if (r.praticien_id !== praticienId) return false
      // Lire l'heure directement depuis la string pour éviter la conversion UTC
      const timePart = r.date_rdv.slice(11, 16) // "08:45"
      const rdvMin = timeToMinutes(timePart)
      const rdvFin = rdvMin + r.duree_minutes
      return t < rdvFin && t + duree > rdvMin
    })
    creneaux.push({ min: t, libre: !occupe })
  }
  return creneaux
}

export default function Agenda() {
  const { praticien } = useAuth()
  const navigate = useNavigate()
  const [rdvs, setRdvs] = useState([])
  const [patients, setPatients] = useState([])
  const [patientSearch, setPatientSearch] = useState('')
  const [patientSearchOpen, setPatientSearchOpen] = useState(false)
  const [patientResults, setPatientResults] = useState([])
  const [patientSearching, setPatientSearching] = useState(false)
  const [parentResults, setParentResults] = useState([])
  const [praticiens, setPraticiens] = useState([])
  const [horairesHab, setHorairesHab] = useState([])
  const [exceptions, setExceptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }))
  const [filterPraticien, setFilterPraticien] = useState('all')
  const didInitFilter = useRef(false)
  useEffect(() => {
    if (!didInitFilter.current && praticien?.id) {
      setFilterPraticien(praticien.id)
      didInitFilter.current = true
    }
  }, [praticien?.id])
  const [panel, setPanel] = useState(null)
  const [panelHoraires, setPanelHoraires] = useState(false)
  const [form, setForm] = useState(emptyForm())
  const [saving, setSaving] = useState(false)
  const [showNewPatient, setShowNewPatient] = useState(false)
  const [newPatient, setNewPatient] = useState({ prenom: '', nom: '', telephone: '', email: '' })
  const [savingPatient, setSavingPatient] = useState(false)
  const [horForm, setHorForm] = useState({ jour_semaine: 1, heure_debut: '09:00', heure_fin: '18:00' })
  const [excForm, setExcForm] = useState({ date_exception: '', type: 'absence', heure_debut: '', heure_fin: '', motif: '' })
  const [panelCreneaux, setPanelCreneaux] = useState(null)
  const [panelConsult, setPanelConsult] = useState(null)
  const [consultForm, setConsultForm] = useState(emptyConsultForm())
  const [savingConsult, setSavingConsult] = useState(false)
  const [savedConsult, setSavedConsult] = useState(false)
  const [consultExistante, setConsultExistante] = useState(null)
  const [factureConsultExistante, setFactureConsultExistante] = useState(null)
  const [historiqueConsult, setHistoriqueConsult] = useState([])
  const [antecedentsConsult, setAntecedentsConsult] = useState([])
  const [patientDetail, setPatientDetail] = useState(null)
  const [patientsAvecConsult, setPatientsAvecConsult] = useState(() => new Set())
  const [panelFactureRapide, setPanelFactureRapide] = useState(null)
  const [factureRapideForm, setFactureRapideForm] = useState({ mode_paiement: '', statut: 'payee', notes: '', emailsSelectionnes: [] })
  const [savingFacture, setSavingFacture] = useState(false)
  const [parentAssoc, setParentAssoc] = useState(null) // { mode:'existing'|'new', search, selectedId, role, prenom, nom, email }

  function emptyForm() {
    return { patient_id: '', praticien_id: praticien?.id || '', date_rdv: '', duree_minutes: 45, motif: '', statut: 'confirme', notes: '' }
  }

  function parseNotes(raw) {
    if (!raw) return ''
    if (raw.startsWith('{') || raw.startsWith('"')) {
      try {
        const parsed = JSON.parse(raw)
        return Object.entries(parsed)
          .filter(([k, v]) => v && typeof v === 'string' && v.trim() && !k.startsWith('select-'))
          .map(([, v]) => v.replace(/\\n/g, '\n').replace(/\\u00e9/g, 'é').replace(/\\u00e8/g, 'è').replace(/\\u00ea/g, 'ê').replace(/\\u00e0/g, 'à'))
          .filter(v => v.trim())
          .join('\n')
      } catch {
        return raw.replace(/\\n/g, '\n').replace(/[{}"]/g, '').replace(/"select-[^"]+":"[^"]*",?/g, '').trim()
      }
    }
    return raw
  }

  function emptyConsultForm() {
    return { motif: '', motif_detail: '', traitement: '', notes_seance: '', duree_minutes: 45, tarif: 65 }
  }

  function couleurIndex(praticienId) {
    if (praticienId in COULEUR_PRATICIEN) return COULEUR_PRATICIEN[praticienId]
    const idx = praticiens.findIndex(p => p.id === praticienId)
    return idx >= 0 ? idx % COULEURS.length : 0
  }

  function couleur(praticienId) {
    return COULEURS[couleurIndex(praticienId)]
  }

  // Praticiennes réellement affichées selon le filtre (ordonnées par couleur fixe), pour le nb de colonnes et leur position
  const praticiensAffiches = (filterPraticien === 'all' ? praticiens : praticiens.filter(p => p.id === filterPraticien))
    .slice().sort((a, b) => couleurIndex(a.id) - couleurIndex(b.id))
  function posIndex(praticienId) {
    const i = praticiensAffiches.findIndex(p => p.id === praticienId)
    return i >= 0 ? i : 0
  }

  const load = useCallback(async () => {
    setLoading(true)
    const debut = weekStart.toISOString()
    const fin = addDays(weekStart, 7).toISOString()
    const debutDate = format(weekStart, 'yyyy-MM-dd')
    const finDate = format(addDays(weekStart, 6), 'yyyy-MM-dd')
    const [rdvRes, horRes, excRes] = await Promise.all([
      supabase.from('rendez_vous').select('*, patients(prenom, nom, date_naissance, enceinte), praticiens(prenom, nom)')
        .gte('date_rdv', debut).lt('date_rdv', fin).order('date_rdv'),
      supabase.from('horaires_habituels').select('*, praticiens(prenom, nom)').order('jour_semaine'),
      supabase.from('horaires_exceptions').select('*, praticiens(prenom, nom)')
        .gte('date_exception', debutDate).lte('date_exception', finDate)
    ])
    setRdvs(rdvRes.data || [])
    setHorairesHab(horRes.data || [])
    setExceptions(excRes.data || [])
    // Étoile "nouveau patient" : patients de la semaine ayant déjà (ou non) une consultation
    const pids = [...new Set((rdvRes.data || []).map(r => r.patient_id).filter(Boolean))]
    if (pids.length) {
      const { data: consults } = await supabase.from('consultations').select('patient_id').in('patient_id', pids)
      setPatientsAvecConsult(new Set((consults || []).map(c => c.patient_id)))
    } else {
      setPatientsAvecConsult(new Set())
    }
    setLoading(false)
  }, [weekStart])

  useEffect(() => { load() }, [load])

  const loadPatients = useCallback(async () => {
    let allPatients = []
    let from = 0
    while (true) {
      const { data: batch } = await supabase.from('patients').select('id, prenom, nom, date_naissance, email').order('nom').range(from, from + 999)
      if (!batch || batch.length === 0) break
      allPatients = [...allPatients, ...batch]
      if (batch.length < 1000) break
      from += 1000
    }
    setPatients(allPatients)
  }, [])

  useEffect(() => {
    loadPatients()
    supabase.from('praticiens').select('id, prenom, nom').order('created_at').then(({ data }) => setPraticiens(data || []))
  }, [loadPatients])

  // Recherche patient CÔTÉ BASE (picker rdv) — instantanée, indépendante du chargement complet
  useEffect(() => {
    const q = patientSearch.trim()
    if (!patientSearchOpen || q.length < 1) { setPatientResults([]); setPatientSearching(false); return }
    let annule = false
    setPatientSearching(true)
    const t = setTimeout(async () => {
      let req = supabase.from('patients').select('id, prenom, nom, date_naissance, email')
      for (const m of q.split(/\s+/).filter(Boolean)) {
        const s = m.replace(/[%,()]/g, ' ').trim()
        if (s) req = req.or(`prenom.ilike.%${s}%,nom.ilike.%${s}%`)
      }
      const { data } = await req.order('nom').order('prenom').limit(50)
      if (!annule) { setPatientResults(data || []); setPatientSearching(false) }
    }, 200)
    return () => { annule = true; clearTimeout(t) }
  }, [patientSearch, patientSearchOpen])

  // Recherche parent CÔTÉ BASE (association d'un mineur)
  useEffect(() => {
    const q = (parentAssoc?.search || '').trim()
    if (!parentAssoc || parentAssoc.mode !== 'existing' || q.length < 2) { setParentResults([]); return }
    let annule = false
    const t = setTimeout(async () => {
      let req = supabase.from('patients').select('id, prenom, nom, date_naissance, email')
      for (const m of q.split(/\s+/).filter(Boolean)) {
        const s = m.replace(/[%,()]/g, ' ').trim()
        if (s) req = req.or(`prenom.ilike.%${s}%,nom.ilike.%${s}%`)
      }
      const { data } = await req.order('nom').order('prenom').limit(50)
      if (!annule) setParentResults((data || []).filter(p => p.id !== panelFactureRapide?.patient?.id))
    }, 200)
    return () => { annule = true; clearTimeout(t) }
  }, [parentAssoc?.search, parentAssoc?.mode, panelFactureRapide?.patient?.id])

  // Déclenche l'auto-save des notes toutes les 30s tant que le panneau consultation est ouvert
  useEffect(() => {
    if (panelConsult !== 'edit' || !consultExistante?.id) return
    const t = setInterval(() => { autoSaveConsult() }, 30000)
    return () => clearInterval(t)
  }, [panelConsult, consultExistante, consultForm])

  useEffect(() => {
    const handler = () => { setForm(emptyForm()); setPanel('new'); setShowNewPatient(false); setPatientSearch('') }
    window.addEventListener('cabinet:new', handler)
    return () => window.removeEventListener('cabinet:new', handler)
  }, [])

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

  function plagesPourJour(day) {
    const jourSemaine = day.getDay() === 0 ? 7 : day.getDay()
    const dateStr = format(day, 'yyyy-MM-dd')
    const excJour = exceptions.filter(e => e.date_exception === dateStr)
    if (excJour.find(e => e.type === 'absence' && !e.heure_debut)) return []
    const plages = horairesHab.filter(h => {
      if (h.jour_semaine !== jourSemaine) return false
      const abs = excJour.filter(e => e.type === 'absence' && e.heure_debut)
      return !abs.some(a =>
        timeToMinutes(a.heure_debut) <= timeToMinutes(h.heure_debut) &&
        timeToMinutes(a.heure_fin) >= timeToMinutes(h.heure_fin)
      )
    })
    const presences = excJour.filter(e => e.type === 'presence')
    let result = [...plages, ...presences]
    if (filterPraticien !== 'all') result = result.filter(p => p.praticien_id === filterPraticien)
    return result
  }

  function rdvsPourJour(day) {
    const dayStr = format(day, 'yyyy-MM-dd')
    return rdvs.filter(r => {
      if (r.date_rdv.slice(0, 10) !== dayStr) return false
      if (filterPraticien !== 'all' && r.praticien_id !== filterPraticien) return false
      return true
    })
  }

  async function createPatientAndSelect() {
    if (!newPatient.prenom || !newPatient.nom) return
    setSavingPatient(true)
    const { data, error } = await supabase.from('patients').insert({
      prenom: newPatient.prenom, nom: newPatient.nom,
      telephone: newPatient.telephone || null, email: newPatient.email || null,
      created_by: praticien.id,
    }).select().single()
    if (!error && data) {
      loadPatients()
      setForm(f => ({ ...f, patient_id: data.id }))
      setShowNewPatient(false)
      setNewPatient({ prenom: '', nom: '', telephone: '', email: '' })
    }
    setSavingPatient(false)
  }

  async function saveRdv() {
    setSaving(true)
    const payload = {
      patient_id: form.patient_id || null, praticien_id: form.praticien_id,
      date_rdv: form.date_rdv, duree_minutes: parseInt(form.duree_minutes),
      motif: form.motif, statut: form.statut, notes: form.notes
    }
    if (panel === 'new') await supabase.from('rendez_vous').insert(payload)
    else await supabase.from('rendez_vous').update(payload).eq('id', panel.id)
    setSaving(false); setPanel(null); load()
  }

  async function deleteRdv(id) {
    if (!window.confirm('Supprimer ce rendez-vous ?')) return
    await supabase.from('rendez_vous').delete().eq('id', id)
    load()
  }

  async function openConsultation(rdv) {
    setAntecedentsConsult([])
    setPatientDetail(null)
    const dateRdv = new Date(rdv.date_rdv)
    const debutJour = new Date(dateRdv); debutJour.setHours(0, 0, 0, 0)
    const finJour = new Date(dateRdv); finJour.setHours(23, 59, 59, 999)
    const { data } = await supabase
      .from('consultations').select('*')
      .eq('patient_id', rdv.patient_id).eq('praticien_id', rdv.praticien_id)
      .gte('date_consultation', debutJour.toISOString())
      .lte('date_consultation', finJour.toISOString())
      .limit(1)
    const consult = data?.[0] || null
    if (consult) {
      setConsultExistante(consult)
      setConsultForm({ motif: consult.motif || '', motif_detail: consult.motif_detail || '', traitement: consult.traitement || '', notes_seance: parseNotes(consult.notes_seance), duree_minutes: consult.duree_minutes, tarif: consult.tarif })
      setPanelConsult('edit')
    } else {
      // Créer la consultation immédiatement pour avoir un ID (schéma corporel)
      const { data: newConsult } = await supabase.from('consultations').insert({
        patient_id: rdv.patient_id, praticien_id: rdv.praticien_id || praticien.id,
        date_consultation: rdv.date_rdv,
        motif: rdv.motif || '', motif_detail: '', traitement: '', notes_seance: '',
        duree_minutes: rdv.duree_minutes || 45, tarif: 65
      }).select().single()
      setConsultExistante(newConsult || null)
      setConsultForm({ motif: rdv.motif || '', motif_detail: '', traitement: '', notes_seance: '', duree_minutes: rdv.duree_minutes || 45, tarif: 65 })
      setPanelConsult('edit')
      if (newConsult) await supabase.from('rendez_vous').update({ statut: 'honore' }).eq('id', rdv.id)
    }
    // Charger l'historique des consultations précédentes
    const { data: histo } = await supabase
      .from('consultations').select('id, date_consultation, motif, tarif')
      .eq('patient_id', rdv.patient_id)
      .order('date_consultation', { ascending: false })
      .limit(20)
    setHistoriqueConsult(histo || [])
    // Antécédents du patient (affichés dans le panneau)
    const { data: antecs } = await supabase
      .from('patient_antecedents').select('categorie, description, date_evenement')
      .eq('patient_id', rdv.patient_id)
    setAntecedentsConsult(antecs || [])
    // Détail patient complet (pour le sexe) — select('*') ne casse pas si une colonne manque
    const { data: pdet } = await supabase.from('patients').select('*').eq('id', rdv.patient_id).single()
    setPatientDetail(pdet || null)
    // Vérifier si une facture existe pour cette consultation
    const consultId = consult?.id || null
    if (consultId) {
      const { data: fex } = await supabase.from('factures').select('id,statut,email_envoye_at').eq('consultation_id', consultId).eq('praticien_id', praticien.id).maybeSingle()
      setFactureConsultExistante(fex || null)
    } else {
      setFactureConsultExistante(null)
    }
    setPanel(null)
  }

  // [UNIFIE-PANEL v1] Ouvre le panneau de consultation directement par son id
  // (arrivée depuis la page Consultations via /agenda?consult=<id>). Ne réécrit pas le panneau, réutilise son état.
  async function openConsultationById(consultId) {
    setAntecedentsConsult([])
    setPatientDetail(null)
    const { data: consult } = await supabase.from('consultations').select('*').eq('id', consultId).single()
    if (!consult) return
    setConsultExistante(consult)
    setConsultForm({ motif: consult.motif || '', motif_detail: consult.motif_detail || '', traitement: consult.traitement || '', notes_seance: parseNotes(consult.notes_seance), duree_minutes: consult.duree_minutes, tarif: consult.tarif })
    setPanelConsult('edit')
    const { data: histo } = await supabase
      .from('consultations').select('id, date_consultation, motif, tarif')
      .eq('patient_id', consult.patient_id)
      .order('date_consultation', { ascending: false }).limit(20)
    setHistoriqueConsult(histo || [])
    const { data: antecs } = await supabase
      .from('patient_antecedents').select('categorie, description, date_evenement')
      .eq('patient_id', consult.patient_id)
    setAntecedentsConsult(antecs || [])
    const { data: pdet } = await supabase.from('patients').select('*').eq('id', consult.patient_id).single()
    setPatientDetail(pdet || null)
    if (praticien?.id) {
      const { data: fex } = await supabase.from('factures').select('id,statut,email_envoye_at').eq('consultation_id', consult.id).eq('praticien_id', praticien.id).maybeSingle()
      setFactureConsultExistante(fex || null)
    } else {
      setFactureConsultExistante(null)
    }
  }

  // [UNIFIE-PANEL v1] Si on arrive avec ?consult=<id> (depuis la page Consultations), ouvrir ce dossier
  const consultParamRef = useRef(false)
  useEffect(() => {
    if (consultParamRef.current || !praticien) return
    const cid = new URLSearchParams(window.location.search).get('consult')
    if (!cid) return
    consultParamRef.current = true
    openConsultationById(cid)
    window.history.replaceState({}, '', '/agenda')
  }, [praticien])

  // Rassemble les adresses mail : celle du patient + celles des responsables liés (patient_liens).
  async function buildDestinataires(pat) {
    const byEmail = new Map()
    const push = (email, nom, principal) => {
      if (!email) return
      const key = String(email).trim().toLowerCase()
      if (!key) return
      if (byEmail.has(key)) {
        const e = byEmail.get(key)
        if (nom && !e.noms.includes(nom)) e.noms.push(nom)
        e.principal = e.principal || !!principal
      } else {
        byEmail.set(key, { email: String(email).trim(), noms: nom ? [nom] : [], principal: !!principal })
      }
    }
    if (pat?.email) push(pat.email, `${pat.prenom || ''} ${pat.nom || ''}`.trim() + ' (patient)', false)
    if (pat?.id) {
      const { data: liens } = await supabase.from('patient_liens')
        .select('lie_id, role, facturation_principale').eq('patient_id', pat.id)
      const ids = (liens || []).map(l => l.lie_id)
      if (ids.length) {
        const { data: resps } = await supabase.from('patients').select('id, prenom, nom, email').in('id', ids)
        const map = {}
        ;(resps || []).forEach(r => { map[r.id] = r })
        for (const lien of (liens || [])) {
          const r = map[lien.lie_id]
          if (r?.email) push(r.email, `${r.prenom || ''} ${r.nom || ''}`.trim() + (lien.role ? ` (${lien.role})` : ' (responsable)'), lien.facturation_principale)
        }
      }
    }
    return [...byEmail.values()].map(e => ({ email: e.email, label: e.noms.join(' / '), principal: e.principal }))
  }

  // Associe un parent à l'enfant : patient existant OU nouvelle fiche rapide. Crée le lien patient_liens.
  async function associerParent() {
    if (!parentAssoc || !panelFactureRapide?.patient?.id) return
    const enfantId = panelFactureRapide.patient.id
    const role = parentAssoc.role || 'tuteur'
    let parent = null
    if (parentAssoc.mode === 'existing') {
      if (!parentAssoc.selectedId) { alert('Choisis un parent dans la liste.'); return }
      parent = parentAssoc.selectedPatient || patients.find(p => p.id === parentAssoc.selectedId)
      if (!parent?.email) { alert("Ce patient n'a pas d'email dans sa fiche."); return }
    } else {
      const prenom = (parentAssoc.prenom || '').trim()
      const nom = (parentAssoc.nom || '').trim()
      const email = (parentAssoc.email || '').trim()
      if (!prenom || !nom || !email) { alert('Renseigne le prénom, le nom et l\'email du parent.'); return }
      const { data: cree, error } = await supabase.from('patients').insert({ prenom, nom, email }).select().single()
      if (error) { alert('Erreur création parent : ' + error.message); return }
      parent = cree
      setPatients(prev => [...prev, cree]) // dispo tout de suite pour la suite
    }
    // Créer le lien (idempotent : on ne duplique pas si déjà présent)
    const { data: dejaLie } = await supabase.from('patient_liens')
      .select('id').eq('patient_id', enfantId).eq('lie_id', parent.id).maybeSingle()
    if (!dejaLie) {
      const { error: eLien } = await supabase.from('patient_liens')
        .insert({ patient_id: enfantId, lie_id: parent.id, role, facturation_principale: true })
      if (eLien) { alert('Erreur création du lien : ' + eLien.message); return }
    }
    // Rafraîchir les destinataires et pré-cocher le parent (fusion si email partagé avec un autre parent)
    const nomParent = `${parent.prenom || ''} ${parent.nom || ''}`.trim() + ` (${role})`
    setPanelFactureRapide(pf => {
      const key = parent.email.toLowerCase()
      const idx = pf.destinataires.findIndex(d => d.email.toLowerCase() === key)
      let destinataires
      if (idx >= 0) {
        destinataires = pf.destinataires.map((d, i) => {
          if (i !== idx) return d
          const noms = d.label ? d.label.split(' / ') : []
          if (!noms.includes(nomParent)) noms.push(nomParent)
          return { ...d, label: noms.join(' / '), principal: true }
        })
      } else {
        destinataires = [...pf.destinataires, { email: parent.email, label: nomParent, principal: true }]
      }
      return { ...pf, destinataires }
    })
    setFactureRapideForm(f => ({
      ...f,
      emailsSelectionnes: f.emailsSelectionnes.includes(parent.email) ? f.emailsSelectionnes : [...f.emailsSelectionnes, parent.email]
    }))
    setParentAssoc(null)
  }

  async function saveFactureRapide() {
    if (!panelFactureRapide || !factureRapideForm.mode_paiement) return
    if (panelFactureRapide.consultationId) {
      const { data: existing } = await supabase.from('factures').select('id')
        .eq('consultation_id', panelFactureRapide.consultationId).eq('praticien_id', praticien.id).single()
      if (existing) { alert('Une facture existe déjà pour cette consultation.'); setPanelFactureRapide(null); return }
    }
    setSavingFacture(true)
    const numero = 'FAC-' + new Date().getFullYear() + '-' + Date.now().toString().slice(-6)
    const { data: nouvelleFacture } = await supabase.from('factures').insert({
      numero, praticien_id: praticien.id, patient_id: panelFactureRapide.patientId,
      consultation_id: panelFactureRapide.consultationId || null,
      date_facture: panelFactureRapide.date, montant: panelFactureRapide.tarif,
      statut: factureRapideForm.statut, mode_paiement: factureRapideForm.mode_paiement,
      date_paiement: factureRapideForm.statut === 'payee' ? panelFactureRapide.date : null,
      notes: factureRapideForm.notes || null,
    }).select().single()
    if (factureRapideForm.statut === 'payee') {
      const { error: eEcriture } = await supabase.from('ecritures_comptables').insert({
        praticien_id: praticien.id, date_ecriture: panelFactureRapide.date,
        libelle: 'Consultation ' + (panelFactureRapide.patient?.prenom || '') + ' ' + (panelFactureRapide.patient?.nom || ''),
        type: 'recette', categorie: 'Honoraires', montant: panelFactureRapide.tarif, source: 'auto',
        facture_id: nouvelleFacture?.id || null, mode_paiement: factureRapideForm.mode_paiement,
      })
      if (eEcriture) alert('⚠ Facture créée, mais la recette comptable n\'a PAS été enregistrée : ' + eEcriture.message + '\nÀ créer manuellement dans Comptabilité, ou réessayer.')
    }
    // Envoyer la facture par email aux destinataires cochés
    const emailsAEnvoyer = [...new Set(factureRapideForm.emailsSelectionnes || [])]
    if (emailsAEnvoyer.length > 0) {
      try {
        const factureObj = {
          numero,
          date_facture: panelFactureRapide.date,
          montant: panelFactureRapide.tarif,
          statut: factureRapideForm.statut,
          date_paiement: factureRapideForm.statut === 'payee' ? panelFactureRapide.date : null,
          notes: factureRapideForm.notes || null,
          patients: panelFactureRapide.patient,
          consultations: null,
        }
        const pdfBase64 = await generateFacturePDF(factureObj, praticien, true)
        let okOne = false
        const echecs = []
        for (const email of emailsAEnvoyer) {
          try {
            const res = await fetch('https://qzaqbhmtmeokgbdywjgx.supabase.co/functions/v1/send-facture', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email,
                prenomPatient: panelFactureRapide.patient.prenom,
                numero,
                dateFacture: panelFactureRapide.date,
                montant: panelFactureRapide.tarif,
                praticienPrenom: praticien.prenom,
                praticienNom: praticien.nom,
                pdfBase64,
                pdfNom: numero + '.pdf',
              })
            })
            if (res.ok) okOne = true
            else { let d = ''; try { d = await res.text() } catch { /* vide */ } echecs.push(email + ' (' + res.status + (d ? ' : ' + d : '') + ')') }
          } catch (e) { echecs.push(email + ' : ' + (e?.message || e)) }
        }
        if (okOne && nouvelleFacture?.id) {
          await supabase.from('factures').update({ email_envoye_at: new Date().toISOString() }).eq('id', nouvelleFacture.id)
        }
        if (echecs.length) alert('Facture créée. Envoi échoué pour :\n- ' + echecs.join('\n- '))
      } catch(e) { alert('Facture créée, mais l\'envoi du mail a échoué : ' + (e?.message || e)) }
    }
    setSavingFacture(false)
    setPanelFactureRapide(null)
    setParentAssoc(null)
    setFactureRapideForm({ mode_paiement: '', statut: 'payee', notes: '', emailsSelectionnes: [] })
  }

  async function saveConsultation(rdv) {
    setSavingConsult(true)
    if (consultExistante?.id) {
      await supabase.from('consultations').update({
        motif: consultForm.motif, motif_detail: consultForm.motif_detail,
        traitement: consultForm.traitement, notes_seance: consultForm.notes_seance,
        duree_minutes: parseInt(consultForm.duree_minutes), tarif: parseFloat(consultForm.tarif)
      }).eq('id', consultExistante.id)
    }
    setSavingConsult(false)
    setSavedConsult(true)
    setTimeout(() => setSavedConsult(false), 2000)
  }

  // Auto-save des notes de consultation (toutes les 30s tant que le panneau est ouvert).
  // Sûr : la consultation existe déjà en base (créée à l'ouverture), on ne fait qu'un update. Ne touche QUE la consultation.
  async function autoSaveConsult() {
    if (!consultExistante?.id) return
    await supabase.from('consultations').update({
      motif: consultForm.motif, motif_detail: consultForm.motif_detail,
      traitement: consultForm.traitement, notes_seance: consultForm.notes_seance,
      duree_minutes: parseInt(consultForm.duree_minutes), tarif: parseFloat(consultForm.tarif)
    }).eq('id', consultExistante.id)
    setSavedConsult(true)
    setTimeout(() => setSavedConsult(false), 1500)
  }

  async function saveHoraire() {
    await supabase.from('horaires_habituels').insert({ ...horForm, praticien_id: praticien.id })
    load()
  }

  async function deleteHoraire(id) {
    await supabase.from('horaires_habituels').delete().eq('id', id)
    load()
  }

  async function saveException() {
    await supabase.from('horaires_exceptions').insert({
      ...excForm, praticien_id: praticien.id,
      heure_debut: excForm.heure_debut || null, heure_fin: excForm.heure_fin || null,
    })
    load()
    setExcForm({ date_exception: '', type: 'absence', heure_debut: '', heure_fin: '', motif: '' })
  }

  async function deleteException(id) {
    await supabase.from('horaires_exceptions').delete().eq('id', id)
    load()
  }

  function handlePlageClick(day, plage) {
    const dateStr = format(day, 'yyyy-MM-dd')
    const dayRdvs = rdvs.filter(r => isSameDay(new Date(r.date_rdv), day))
    const duree = 45
    const creneaux = creneauxLibres(plage.heure_debut, plage.heure_fin, duree, dayRdvs, plage.praticien_id)
    setPanelCreneaux({ day, plage, creneaux, dateStr, duree })
  }

  function selectCreneau(creneau) {
    const { dateStr, plage, duree } = panelCreneaux
    const heureStr = minutesToTime(creneau.min)
    const dateRdv = dateStr + 'T' + heureStr
    setForm({ ...emptyForm(), praticien_id: plage.praticien_id, date_rdv: dateRdv, duree_minutes: duree })
    setPanelCreneaux(null)
    setPanel('new')
    setShowNewPatient(false)
    setPatientSearch('')
    setPatientSearchOpen(false)
  }

  const today = new Date()
  const totalHeight = (HEURE_FIN - HEURE_DEBUT) * PX_PAR_HEURE

  return (
    <>
      {/* Barre navigation */}
      <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:16, flexWrap:'wrap'}}>
        <button className="btn btn-sm" onClick={() => setWeekStart(addDays(weekStart, -7))}>‹</button>
        <span style={{fontWeight:500, fontSize:13, minWidth:220}}>
          {format(weekStart, 'd MMM', {locale:fr})} — {format(addDays(weekStart, 6), 'd MMM yyyy', {locale:fr})}
        </span>
        <button className="btn btn-sm" onClick={() => setWeekStart(addDays(weekStart, 7))}>›</button>
        <button className="btn btn-sm" onClick={() => setWeekStart(startOfWeek(new Date(), {weekStartsOn:1}))}>Aujourd'hui</button>
        <div style={{marginLeft:'auto', display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
          {praticiens.map((p, i) => (
            <div key={p.id} style={{display:'flex', alignItems:'center', gap:5, fontSize:12}}>
              <div style={{width:10, height:10, borderRadius:2, background: COULEURS[couleurIndex(p.id)].border}} />
              <span style={{color:'var(--text-2)'}}>{p.prenom}</span>
            </div>
          ))}
          <select className="btn btn-sm" value={filterPraticien} onChange={e => setFilterPraticien(e.target.value)} style={{border:'0.5px solid var(--border-md)'}}>
            <option value="all">Toutes</option>
            {praticiens.map(p => <option key={p.id} value={p.id}>{p.prenom}</option>)}
          </select>
          <button className="btn btn-sm" onClick={() => setPanelHoraires(true)}>⚙ Mes horaires</button>
        </div>
      </div>

      {/* Grille semaine */}
      {loading ? <div className="empty">Chargement…</div> : (
        <div className="card" style={{padding:0, overflow:'hidden'}}>
          {/* En-têtes jours */}
          <div style={{display:'grid', gridTemplateColumns:'44px repeat(7, 1fr)', borderBottom:'0.5px solid var(--border)'}}>
            <div />
            {days.map((day, i) => {
              const isToday = isSameDay(day, today)
              const isWeekend = i >= 5
              return (
                <div key={i} style={{textAlign:'center', padding:'8px 4px', opacity: isWeekend ? .5 : 1, borderLeft:'0.5px solid var(--border)', background: isToday ? 'var(--bg)' : 'transparent'}}>
                  <div style={{fontSize:10, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'.05em'}}>{format(day, 'EEE', {locale:fr})}</div>
                  <div style={{width:26, height:26, borderRadius:'50%', margin:'3px auto 0', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:500, background: isToday ? 'var(--text)' : 'transparent', color: isToday ? 'var(--surface)' : 'var(--text)'}}>
                    {format(day, 'd')}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Corps grille */}
          <div style={{display:'grid', gridTemplateColumns:'44px repeat(7, 1fr)', overflowY:'auto', maxHeight:'calc(100vh - 220px)'}}>          
            {/* Heures */}
            <div style={{position:'relative', height:totalHeight}}>
              {Array.from({length: HEURE_FIN - HEURE_DEBUT}, (_, i) => (
                <div key={i} style={{position:'absolute', top: i * PX_PAR_HEURE - 7, right:6, fontSize:10, color:'var(--text-3)'}}>
                  {String(HEURE_DEBUT + i).padStart(2, '0')}h
                </div>
              ))}
            </div>

            {/* Colonnes jours */}
            {days.map((day, di) => {
              const isWeekend = di >= 5
              const plages = plagesPourJour(day)
              const dayRdvs = rdvsPourJour(day)
              const nbPrat = praticiensAffiches.length
              return (
                <div key={di} style={{position:'relative', height:totalHeight, borderLeft:'0.5px solid var(--border)', opacity: isWeekend ? .65 : 1, background: isWeekend ? 'var(--bg)' : 'transparent'}}>
                  {/* Lignes heures */}
                  {Array.from({length: HEURE_FIN - HEURE_DEBUT}, (_, i) => (
                    <div key={i} style={{position:'absolute', top: i * PX_PAR_HEURE, left:0, right:0, borderTop:'0.5px solid var(--border)', opacity:.4}} />
                  ))}

                  {/* Plages présence */}
                  {plages.map((plage, pi) => {
                    const coul = couleur(plage.praticien_id)
                    const top = minutesToPx(timeToMinutes(plage.heure_debut))
                    const height = minutesToPx(timeToMinutes(plage.heure_fin)) - top
                    if (height <= 0) return null
                    const idx = posIndex(plage.praticien_id)
                    const w = nbPrat > 1 ? (100 / nbPrat) + '%' : '100%'
                    const l = nbPrat > 1 ? (idx * 100 / nbPrat) + '%' : '0'
                    return (
                      <div key={pi} onClick={() => handlePlageClick(day, plage)}
                        title="Cliquer pour voir les créneaux"
                        style={{position:'absolute', top, left:l, width:w, height, background:coul.bg, borderLeft:'3px solid ' + coul.border, opacity:.75, cursor:'pointer', zIndex:1, transition:'opacity .1s'}}
                        onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '.75'}
                      />
                    )
                  })}

                  {/* RDV */}
                  {dayRdvs.map(r => {
                    // Lire l'heure directement depuis la string sans conversion UTC
                    const dateStr = r.date_rdv.slice(0, 16) // "2026-03-30T08:45"
                    const timePart = dateStr.slice(11, 16)   // "08:45"
                    const [hh, mm] = timePart.split(':').map(Number)
                    const minDebut = hh * 60 + mm
                    const top = minutesToPx(minDebut)
                    const height = Math.max((r.duree_minutes / 60) * PX_PAR_HEURE - 4, 18)
                    const coul = couleur(r.praticien_id)
                    const idx = posIndex(r.praticien_id)
                    const w = nbPrat > 1 ? 'calc(' + (100 / nbPrat) + '% - 6px)' : 'calc(100% - 8px)'
                    const l = nbPrat > 1 ? 'calc(' + (idx * 100 / nbPrat) + '% + 3px)' : '4px'
                    const profil = profilPatient(r.patients)
                    const nouveauPatient = r.patient_id && !patientsAvecConsult.has(r.patient_id)
                    const age = getAge(r.patients?.date_naissance)
                    const dob = r.patients?.date_naissance
                      ? new Date(r.patients.date_naissance).toLocaleDateString('fr-FR', {day:'2-digit', month:'2-digit', year:'numeric'})
                      : null
                    return (
                      <div key={r.id}
                        onClick={() => { setForm({...r, date_rdv: r.date_rdv.slice(0,16), patient_id: r.patient_id||'', notes: r.notes||''}); setPanel(r); setShowNewPatient(false); setPatientSearch(r.patients ? (r.patients.prenom + ' ' + r.patients.nom) : '') }}
                        style={{position:'absolute', top: top + 2, left:l, width:w, height, background:coul.border, borderRadius:4, padding:'3px 6px', cursor:'pointer', overflow:'hidden', zIndex:2, boxSizing:'border-box'}}>
                        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:2}}>
                          <span style={{fontSize:10, fontWeight:600, color:'#fff'}}>{timePart}</span>
                          <span style={{display:'flex', alignItems:'center', gap:3}}>
                            {nouveauPatient && <span style={{fontSize:11, lineHeight:1}} title="Nouveau patient (1re visite)">⭐</span>}
                            {profil && <span style={{fontSize:12, lineHeight:1}} title={profil.label}>{profil.symbole}</span>}
                          </span>
                        </div>
                        {height > 24 && (
                          <div style={{fontSize:10, fontWeight:600, color:'#fff', lineHeight:1.2, marginTop:1, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis'}}>
                            {r.patients?.prenom} {r.patients?.nom || 'Ext.'}
                          </div>
                        )}
                        {height > 44 && dob && (
                          <div style={{fontSize:9, color:'rgba(255,255,255,.8)', lineHeight:1.2, marginTop:1}}>
                            {dob}{age !== null ? ' (' + age + ' ans)' : ''}
                          </div>
                        )}
                        {height > 60 && r.motif && (
                          <div style={{fontSize:9, color:'rgba(255,255,255,.75)', marginTop:1, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis'}}>{r.motif}</div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* PANEL RDV */}
      {panel && (
        <>
          <div className="panel-overlay" onClick={() => setPanel(null)} />
          <div className="panel">
            <div className="panel-header">
              <h3>{panel === 'new' ? 'Nouveau rendez-vous' : 'Modifier le RDV'}</h3>
              <button className="close-btn" onClick={() => setPanel(null)}>✕</button>
            </div>
            <div className="panel-body">
              <div className="form-group" style={{marginBottom: showNewPatient ? 8 : 14}}>
                <label>Patient</label>
                <div style={{display:'flex', gap:6}}>
                  <div style={{flex:1, position:'relative'}}>
                    <input
                      placeholder="Rechercher par nom ou prénom…"
                      value={patientSearch}
                      autoComplete="off"
                      onChange={e => {
                        setPatientSearch(e.target.value)
                        setForm(f => ({...f, patient_id: ''}))
                        setShowNewPatient(false)
                      }}
                      onFocus={() => setPatientSearchOpen(true)}
                      style={{width:'100%'}}
                    />
                    {patientSearchOpen && patientSearch.length >= 1 && (
                      <div style={{position:'absolute', top:'100%', left:0, right:0, zIndex:100, background:'var(--surface)', border:'1px solid var(--border-md)', borderRadius:'var(--radius)', maxHeight:200, overflowY:'auto', boxShadow:'0 4px 12px rgba(0,0,0,.1)'}}>
                        {patientResults.map(p => (
                          <div key={p.id}
                            onMouseDown={() => {
                              setForm(f => ({...f, patient_id: p.id}))
                              setPatientSearch(p.prenom + ' ' + p.nom)
                              setPatientSearchOpen(false)
                              setShowNewPatient(false)
                            }}
                            style={{padding:'8px 12px', cursor:'pointer', fontSize:13, borderBottom:'0.5px solid var(--border)'}}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}>
                            <span style={{fontWeight:500}}>{p.prenom} {p.nom}</span>
                            {p.date_naissance && (
                              <span style={{fontSize:11, color:'var(--text-3)', marginLeft:8}}>
                                {new Date(p.date_naissance).toLocaleDateString('fr-FR', {day:'2-digit', month:'2-digit', year:'numeric'})}
                              </span>
                            )}
                          </div>
                        ))}
                        {patientSearching && (
                          <div style={{padding:'8px 12px', fontSize:12, color:'var(--text-3)'}}>Recherche…</div>
                        )}
                        {!patientSearching && patientResults.length === 0 && (
                          <div style={{padding:'8px 12px', fontSize:12, color:'var(--text-3)'}}>Aucun patient trouvé</div>
                        )}
                      </div>
                    )}
                  </div>
                  <button className="btn btn-sm" onClick={() => { setShowNewPatient(v => !v); setForm(f => ({...f, patient_id:''})); setPatientSearch('') }}>
                    {showNewPatient ? '✕' : '+ Nouveau'}
                  </button>
                </div>
                {form.patient_id && (
                  <div style={{fontSize:11, color:'var(--green-text)', marginTop:3}}>✓ Patient sélectionné</div>
                )}
              </div>
              {showNewPatient && (
                <div style={{background:'var(--bg)', border:'0.5px solid var(--border-md)', borderRadius:'var(--radius)', padding:'12px 14px', marginBottom:14}}>
                  <div style={{fontSize:12, fontWeight:500, color:'var(--text-2)', marginBottom:10}}>Nouveau patient</div>
                  <div className="form-row" style={{marginBottom:8}}>
                    <div className="form-group"><label>Prénom *</label><input value={newPatient.prenom} onChange={e => setNewPatient(p => ({...p, prenom: e.target.value}))} autoFocus /></div>
                    <div className="form-group"><label>Nom *</label><input value={newPatient.nom} onChange={e => setNewPatient(p => ({...p, nom: e.target.value}))} /></div>
                  </div>
                  <div className="form-row" style={{marginBottom:10}}>
                    <div className="form-group"><label>Téléphone</label><input value={newPatient.telephone} onChange={e => setNewPatient(p => ({...p, telephone: e.target.value}))} /></div>
                    <div className="form-group"><label>Email</label><input type="email" value={newPatient.email} onChange={e => setNewPatient(p => ({...p, email: e.target.value}))} /></div>
                  </div>
                  <button className="btn btn-primary btn-sm" style={{width:'100%', justifyContent:'center'}} onClick={createPatientAndSelect} disabled={savingPatient || !newPatient.prenom || !newPatient.nom}>
                    {savingPatient ? 'Création…' : 'Créer et sélectionner'}
                  </button>
                </div>
              )}
              <div className="form-group" style={{marginBottom:14}}>
                <label>Praticienne *</label>
                <select value={form.praticien_id} onChange={e => setForm({...form, praticien_id: e.target.value})}>
                  {praticiens.map(p => <option key={p.id} value={p.id}>{p.prenom} {p.nom}</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Date et heure *</label><input type="datetime-local" value={form.date_rdv} onChange={e => setForm({...form, date_rdv: e.target.value})} /></div>
                <div className="form-group">
                  <label>Durée</label>
                  <select value={form.duree_minutes} onChange={e => setForm({...form, duree_minutes: e.target.value})}>
                    <option value={30}>30 min</option><option value={45}>45 min</option>
                    <option value={60}>60 min</option><option value={90}>90 min</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Motif</label><input value={form.motif} onChange={e => setForm({...form, motif: e.target.value})} /></div>
                <div className="form-group">
                  <label>Statut</label>
                  <select value={form.statut} onChange={e => setForm({...form, statut: e.target.value})}>
                    <option value="confirme">Confirmé</option><option value="en_attente">En attente</option>
                    <option value="honore">Honoré</option><option value="annule">Annulé</option>
                  </select>
                </div>
              </div>
              <div className="form-group"><label>Notes</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
            </div>
            <div className="panel-footer">
              {panel !== 'new' && <button className="btn btn-sm btn-danger" onClick={() => deleteRdv(panel.id)}>Supprimer</button>}
              <div style={{flex:1}} />
              {panel !== 'new' && panel.patient_id && (
                <button className="btn btn-sm" onClick={() => openConsultation(panel)}
                  style={{background:'#1D9E75', color:'#fff', border:'none', fontWeight:500}}>
                  📋 Consultation
                </button>
              )}
              <button className="btn btn-sm" onClick={() => setPanel(null)}>Annuler</button>
              <button className="btn btn-primary btn-sm" onClick={saveRdv} disabled={saving || !form.date_rdv}>
                {saving ? '…' : panel === 'new' ? 'Créer' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* PANEL CONSULTATION */}
      {panelConsult && (() => {
        const rdv = panelConsult === 'edit' ? null : panelConsult.rdv
        const patientId = rdv ? rdv.patient_id : consultExistante?.patient_id
        const patient = patients.find(p => p.id === patientId)
        const isEdit = panelConsult === 'edit'
        const dateConsult = isEdit ? new Date(consultExistante.date_consultation) : rdv ? new Date(rdv.date_rdv) : new Date()
        return (
          <>
            <div className="panel-overlay" onClick={() => setPanelConsult(null)} />
            <div className="panel" style={{width:'calc(100vw - 240px)', maxWidth:1400, left:'240px', right:0, transform:'none', borderRadius:0, height:'100vh', top:0}} onClick={e => e.stopPropagation()}>
              <div className="panel-header" style={{background:'var(--bg)'}}>
                <div style={{display:'flex', alignItems:'center', gap:14}}>
                  <div style={{width:44, height:44, borderRadius:'50%', flexShrink:0, background:'#1D9E75', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:600}}>
                    {patient ? (patient.prenom[0] + patient.nom[0]).toUpperCase() : '?'}
                  </div>
                  <div>
                    <div style={{fontSize:18, fontWeight:600, color:'var(--text)', display:'flex', alignItems:'center', gap:10}}>
                      {patient ? patient.prenom + ' ' + patient.nom : 'Patient inconnu'}
                      {patient && (
                        <span onClick={() => { localStorage.setItem('open_patient_id', patient.id); navigate('/patients') }}
                          style={{fontSize:11, color:'var(--primary)', textDecoration:'underline', cursor:'pointer', fontWeight:400}}>
                          → Ouvrir la fiche
                        </span>
                      )}
                    </div>
                    <div style={{fontSize:12, color:'var(--text-2)', marginTop:2}}>
                      {format(dateConsult, "EEEE d MMMM yyyy 'à' HH:mm", {locale:fr})} — {isEdit ? 'Modification' : 'Nouvelle consultation'}
                    </div>
                    {patient && (() => {
                      const age = getAge(patient.date_naissance)
                      const g = patientDetail?.genre || patientDetail?.sexe
                      const sexe = g === 'F' ? 'Femme' : g === 'M' || g === 'H' ? 'Homme' : null
                      const dobTxt = patient.date_naissance
                        ? new Date(patient.date_naissance).toLocaleDateString('fr-FR', {day:'2-digit', month:'2-digit', year:'numeric'})
                        : null
                      const bits = [
                        age !== null ? age + ' ans' : null,
                        sexe,
                        dobTxt ? 'né(e) le ' + dobTxt : null,
                      ].filter(Boolean)
                      return bits.length ? (
                        <div style={{fontSize:12, color:'var(--text)', fontWeight:500, marginTop:2}}>{bits.join(' · ')}</div>
                      ) : null
                    })()}
                  </div>
                </div>
                <div style={{display:'flex', alignItems:'center', gap:8}}>
                  {savedConsult && <span style={{fontSize:11, color:'#2E7D32', fontWeight:500}}>✓ Sauvegardé</span>}
                  <button className="close-btn" onClick={() => setPanelConsult(null)}>✕</button>
                </div>
              </div>
              <div className="panel-body" style={{display:'grid', gridTemplateColumns:'1fr 260px 240px', gap:20, alignItems:'start', height:'calc(100vh - 120px)', overflowY:'auto'}}>
                {/* Colonne 1 : formulaire */}
                <div>
                  <div className="form-row" style={{marginBottom:16}}>
                    <div className="form-group">
                      <label>Durée</label>
                      <select value={consultForm.duree_minutes} onChange={e => setConsultForm(f => ({...f, duree_minutes: e.target.value}))}>
                        <option value={30}>30 min</option><option value={45}>45 min</option>
                        <option value={60}>60 min</option><option value={90}>90 min</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Tarif (€)</label>
                      <input type="number" value={consultForm.tarif} onChange={e => setConsultForm(f => ({...f, tarif: e.target.value}))} />
                    </div>
                  </div>
                  <div className="form-group" style={{marginBottom:14}}>
                    <label>Motif</label>
                    <input value={consultForm.motif} onChange={e => setConsultForm(f => ({...f, motif: e.target.value}))} placeholder="Ex: Douleur lombaire, suivi cervical…" autoFocus />
                  </div>
                  <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14}}>
                    <div className="form-group">
                      <label>Détail du motif</label>
                      <textarea value={consultForm.motif_detail} onChange={e => setConsultForm(f => ({...f, motif_detail: e.target.value}))} placeholder="Description complète, localisation, depuis quand…" style={{minHeight:140, resize:'vertical'}} />
                    </div>
                    <div className="form-group">
                      <label>Traitement</label>
                      <textarea value={consultForm.traitement} onChange={e => setConsultForm(f => ({...f, traitement: e.target.value}))} placeholder="Techniques utilisées, zones travaillées…" style={{minHeight:140, resize:'vertical'}} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea value={consultForm.notes_seance} onChange={e => setConsultForm(f => ({...f, notes_seance: e.target.value}))} placeholder="Évolution, suite à donner, conseils…" style={{minHeight:80, resize:'vertical'}} />
                  </div>
                </div>
                {/* Colonne 2 : schéma corporel */}
                <div style={{borderLeft:'0.5px solid var(--border)', paddingLeft:16}}>
                  <div style={{fontSize:12, fontWeight:600, color:'var(--text-2)', marginBottom:8}}>Schéma corporel</div>
                  {patientId
                    ? <SchemaCorporel patientId={patientId} consultationId={consultExistante?.id || null} readOnly={false} />
                    : <div style={{fontSize:12, color:'var(--text-3)'}}>Aucun patient</div>
                  }
                </div>
                {/* Colonne 3 : antécédents + historique */}
                <div style={{borderLeft:'0.5px solid var(--border)', paddingLeft:16}}>
                  <div style={{fontSize:12, fontWeight:600, color:'var(--text-2)', marginBottom:8}}>
                    Antécédents {antecedentsConsult.length > 0 ? `(${antecedentsConsult.length})` : ''}
                  </div>
                  {antecedentsConsult.length === 0 ? (
                    <div style={{fontSize:12, color:'var(--text-3)', marginBottom:14}}>Aucun antécédent</div>
                  ) : (
                    <div style={{display:'flex', flexDirection:'column', gap:3, marginBottom:14, maxHeight:180, overflowY:'auto'}}>
                      {antecedentsConsult.map((a, i) => (
                        <div key={i} style={{padding:'5px 8px', borderRadius:5, background:'#FFF6E9', border:'0.5px solid #F0D9B5', fontSize:11}}>
                          <span style={{fontWeight:600, color:'#8A5A00', textTransform:'capitalize'}}>{a.categorie || 'antécédent'}</span>
                          {a.description ? <span style={{color:'var(--text)'}}> — {a.description}</span> : null}
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{fontSize:12, fontWeight:600, color:'var(--text-2)', marginBottom:8}}>
                    Historique {historiqueConsult.length > 0 ? `(${historiqueConsult.length})` : ''}
                  </div>
                  {historiqueConsult.length === 0 ? (
                    <div style={{fontSize:12, color:'var(--text-3)'}}>Aucune consultation précédente</div>
                  ) : (
                    <div style={{display:'flex', flexDirection:'column', gap:3, overflowY:'auto', maxHeight:'calc(100vh - 180px)'}}>
                      {historiqueConsult.map(h => (
                        <div key={h.id}
                          style={{padding:'5px 8px', borderRadius:5, background:'var(--surface)', border:'0.5px solid var(--border)', cursor:'pointer', fontSize:11}}
                          onClick={async () => {
                            const { data: full } = await supabase.from('consultations').select('*').eq('id', h.id).single()
                            if (full) {
                              setConsultExistante(full)
                              setConsultForm({ motif: full.motif||'', motif_detail: full.motif_detail||'', traitement: full.traitement||'', notes_seance: parseNotes(full.notes_seance), duree_minutes: full.duree_minutes, tarif: full.tarif })
                              setPanelConsult('edit')
                            }
                          }}
                          onMouseEnter={e => e.currentTarget.style.background='var(--bg)'}
                          onMouseLeave={e => e.currentTarget.style.background='var(--surface)'}>
                          <div style={{fontWeight:500, color:'var(--text-2)'}}>
                            {format(new Date(h.date_consultation), 'dd/MM/yyyy', {locale:fr})}
                          </div>
                          {h.motif && <div style={{color:'var(--text-3)', marginTop:1, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis'}}>{h.motif}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="panel-footer" style={{gap:10}}>
                <button className="btn btn-sm" onClick={() => setPanelConsult(null)}>Annuler</button>
                <div style={{flex:1}} />
                {factureConsultExistante ? (
                  <button className="btn btn-sm" style={{background:'#E8F5E9', color:'#2E7D32', border:'1px solid #A5D6A7', fontWeight:600, cursor:'default'}} disabled>
                    ✓ Facturée
                  </button>
                ) : (
                  <button className="btn btn-sm" style={{background:'#185FA5', color:'#fff', border:'none', fontWeight:500}}
                    onClick={async () => {
                      const consultId = isEdit ? consultExistante?.id : null
                      await saveConsultation()
                      const dest = await buildDestinataires(patient)
                      const estMineur = patient?.date_naissance ? getAge(patient.date_naissance) < 18 : false
                      const principaux = dest.filter(d => d.principal).map(d => d.email)
                      const preselect = principaux.length ? principaux : (patient?.email ? [patient.email] : [])
                      setParentAssoc(null)
                      setFactureRapideForm({ mode_paiement: '', statut: 'payee', notes: '', emailsSelectionnes: preselect })
                      setPanelFactureRapide({ patient, patientId, consultationId: consultId, tarif: parseFloat(consultForm.tarif), date: format(dateConsult, 'yyyy-MM-dd'), motif: consultForm.motif, destinataires: dest, estMineur })
                    }}
                    disabled={savingConsult}>
                    💳 Enregistrer + Facturer
                  </button>
                )}
                <button className="btn btn-primary btn-sm" onClick={() => saveConsultation()} disabled={savingConsult}>
                  {savingConsult ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </>
        )
      })()}

      {/* FACTURE RAPIDE */}
      {panelFactureRapide && (
        <>
          <div className="panel-overlay" onClick={() => setPanelFactureRapide(null)} />
          <div className="panel" style={{width:520}}>
            <div className="panel-header">
              <div>
                <h3>Établir la facture</h3>
                <div style={{fontSize:12, color:'var(--text-2)', marginTop:2}}>
                  {panelFactureRapide.patient?.prenom} {panelFactureRapide.patient?.nom}
                </div>
              </div>
              <button className="close-btn" onClick={() => setPanelFactureRapide(null)}>✕</button>
            </div>
            <div className="panel-body">
              <div style={{background:'var(--bg)', borderRadius:'var(--radius)', padding:'14px 16px', marginBottom:20}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                  <div>
                    <div style={{fontSize:14, fontWeight:500}}>{panelFactureRapide.patient?.prenom} {panelFactureRapide.patient?.nom}</div>
                    {panelFactureRapide.motif && <div style={{fontSize:12, color:'var(--text-2)', marginTop:2}}>{panelFactureRapide.motif}</div>}
                    <div style={{fontSize:12, color:'var(--text-2)', marginTop:2}}>{panelFactureRapide.date}</div>
                  </div>
                  <div style={{fontSize:24, fontWeight:600}}>{panelFactureRapide.tarif} €</div>
                </div>
              </div>
              <div className="form-row" style={{marginBottom:14}}>
                <div className="form-group">
                  <label>Mode de paiement *</label>
                  <select value={factureRapideForm.mode_paiement} onChange={e => setFactureRapideForm(f => ({...f, mode_paiement: e.target.value}))}>
                    <option value="">— Choisir —</option>
                    <option value="especes">Espèces</option>
                    <option value="cheque">Chèque</option>
                    <option value="cb">Carte bancaire</option>
                    <option value="virement">Virement</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select value={factureRapideForm.statut} onChange={e => setFactureRapideForm(f => ({...f, statut: e.target.value}))}>
                    <option value="payee">Payée ✓</option>
                    <option value="en_attente">En attente</option>
                  </select>
                </div>
              </div>
              {factureRapideForm.statut === 'payee' && (
                <div style={{display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'#EAF3DE', borderRadius:'var(--radius)', marginBottom:14}}>
                  <span style={{fontSize:13, color:'#27500A', fontWeight:500}}>✅ Facture marquée comme payée</span>
                </div>
              )}
              <div className="form-group">
                <label>Notes (optionnel)</label>
                <input value={factureRapideForm.notes} onChange={e => setFactureRapideForm(f => ({...f, notes: e.target.value}))} placeholder="Référence chèque, numéro de transaction…" />
              </div>

              {/* Destinataires du mail */}
              <div style={{marginBottom:4}}>
                <label style={{fontSize:12, color:'var(--text-2)', fontWeight:500}}>Envoyer la facture par mail à :</label>
                {panelFactureRapide.destinataires.length === 0 && (
                  <div style={{fontSize:12, color: panelFactureRapide.estMineur ? 'var(--text-2)' : '#C62828', marginTop:4}}>
                    {panelFactureRapide.estMineur ? 'Aucun responsable associé pour ce mineur — associe un parent ci-dessous.' : "Aucune adresse mail enregistrée."}
                  </div>
                )}
                <div style={{display:'flex', flexDirection:'column', gap:4, marginTop:4}}>
                  {panelFactureRapide.destinataires.map(d => (
                    <label key={d.email} style={{display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer'}}>
                      <input type="checkbox"
                        checked={factureRapideForm.emailsSelectionnes.includes(d.email)}
                        onChange={e => setFactureRapideForm(f => ({...f, emailsSelectionnes: e.target.checked ? [...f.emailsSelectionnes, d.email] : f.emailsSelectionnes.filter(x => x !== d.email)}))} />
                      <span>{d.label}{d.principal ? ' ★' : ''} — <span style={{color:'var(--text-2)'}}>{d.email}</span></span>
                    </label>
                  ))}
                </div>
                {panelFactureRapide.estMineur && (
                  <button className="btn btn-sm" style={{marginTop:6}}
                    onClick={() => setParentAssoc({ mode: 'existing', search: '', selectedId: '', role: 'mere', prenom: '', nom: '', email: '' })}>
                    + Associer un parent
                  </button>
                )}
              </div>
            </div>
            <div className="panel-footer">
              <button className="btn btn-sm" onClick={() => setPanelFactureRapide(null)}>Passer</button>
              <div style={{flex:1}} />
              <button className="btn btn-primary btn-sm" style={{background:'#185FA5', border:'none'}}
                onClick={saveFactureRapide} disabled={savingFacture || !factureRapideForm.mode_paiement}>
                {savingFacture ? 'Création…' : '💳 Créer la facture'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* PANEL ASSOCIER UN PARENT (au-dessus du modal facture) */}
      {parentAssoc && (
        <>
          <div className="panel-overlay" style={{zIndex:1000}} onClick={() => setParentAssoc(null)} />
          <div className="panel" style={{zIndex:1001, width:440}}>
            <div className="panel-header">
              <h3>Associer un parent</h3>
              <button className="close-btn" onClick={() => setParentAssoc(null)}>✕</button>
            </div>
            <div className="panel-body">
              <div style={{display:'flex', gap:8, marginBottom:12}}>
                <button className="btn btn-sm" style={{border:'none', background: parentAssoc.mode==='existing' ? '#185FA5' : '#f0f0f0', color: parentAssoc.mode==='existing' ? '#fff' : '#333', fontWeight: parentAssoc.mode==='existing' ? 700 : 400}}
                  onClick={() => setParentAssoc(a => ({...a, mode:'existing'}))}>Déjà patient</button>
                <button className="btn btn-sm" style={{border:'none', background: parentAssoc.mode==='new' ? '#185FA5' : '#f0f0f0', color: parentAssoc.mode==='new' ? '#fff' : '#333', fontWeight: parentAssoc.mode==='new' ? 700 : 400}}
                  onClick={() => setParentAssoc(a => ({...a, mode:'new'}))}>Nouveau</button>
              </div>

              <div className="form-group" style={{marginBottom:12}}>
                <label>Lien</label>
                <select value={parentAssoc.role} onChange={e => setParentAssoc(a => ({...a, role: e.target.value}))}>
                  <option value="mere">Mère</option>
                  <option value="pere">Père</option>
                  <option value="tuteur">Tuteur</option>
                </select>
              </div>

              {parentAssoc.mode === 'existing' ? (
                <div className="form-group">
                  <label>Rechercher un patient</label>
                  <input value={parentAssoc.search} onChange={e => setParentAssoc(a => ({...a, search: e.target.value, selectedId: ''}))} placeholder="Nom ou prénom…" />
                  {parentAssoc.search.trim().length >= 2 && (
                    <div style={{maxHeight:180, overflowY:'auto', border:'1px solid var(--border)', borderRadius:6, marginTop:4}}>
                      {parentResults.map(p => (
                        <div key={p.id} onClick={() => setParentAssoc(a => ({...a, selectedId: p.id, selectedPatient: p}))}
                          style={{padding:'6px 10px', cursor:'pointer', fontSize:13, background: parentAssoc.selectedId === p.id ? '#E3F2FD' : 'transparent'}}>
                          {p.prenom} {p.nom} {p.email ? <span style={{color:'var(--text-2)'}}>· {p.email}</span> : <span style={{color:'#C62828'}}>· pas d'email</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{display:'flex', flexDirection:'column', gap:8}}>
                  <div className="form-group"><label>Prénom</label><input value={parentAssoc.prenom} onChange={e => setParentAssoc(a => ({...a, prenom: e.target.value}))} /></div>
                  <div className="form-group"><label>Nom</label><input value={parentAssoc.nom} onChange={e => setParentAssoc(a => ({...a, nom: e.target.value}))} /></div>
                  <div className="form-group"><label>Email</label><input type="email" value={parentAssoc.email} onChange={e => setParentAssoc(a => ({...a, email: e.target.value}))} /></div>
                  <div style={{fontSize:11, color:'var(--text-3)'}}>L'adresse postale reste celle de la fiche de l'enfant.</div>
                </div>
              )}
            </div>
            <div className="panel-footer">
              <button className="btn btn-sm" onClick={() => setParentAssoc(null)}>Annuler</button>
              <div style={{flex:1}} />
              <button className="btn btn-primary btn-sm" style={{background:'#1D9E75', border:'none'}} onClick={associerParent}>Associer</button>
            </div>
          </div>
        </>
      )}

      {/* PANEL CRÉNEAUX */}
      {panelCreneaux && (
        <>
          <div className="panel-overlay" onClick={() => setPanelCreneaux(null)} />
          <div className="panel">
            <div className="panel-header">
              <div>
                <h3>Créneaux disponibles</h3>
                <div style={{fontSize:12, color:'var(--text-2)', marginTop:2}}>
                  {format(panelCreneaux.day, 'EEEE d MMMM', {locale:fr})} · {panelCreneaux.plage.heure_debut?.slice(0,5)} – {panelCreneaux.plage.heure_fin?.slice(0,5)}
                </div>
              </div>
              <button className="close-btn" onClick={() => setPanelCreneaux(null)}>✕</button>
            </div>
            <div className="panel-body">
              <div className="form-group" style={{marginBottom:16}}>
                <label>Durée de la séance</label>
                <select value={panelCreneaux.duree} onChange={e => {
                  const duree = parseInt(e.target.value)
                  const dayRdvs = rdvs.filter(r => isSameDay(new Date(r.date_rdv), panelCreneaux.day))
                  const creneaux = creneauxLibres(panelCreneaux.plage.heure_debut, panelCreneaux.plage.heure_fin, duree, dayRdvs, panelCreneaux.plage.praticien_id)
                  setPanelCreneaux(p => ({...p, duree, creneaux}))
                }}>
                  <option value={30}>30 min</option><option value={45}>45 min</option>
                  <option value={60}>60 min</option><option value={90}>90 min</option>
                </select>
              </div>
              <div style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:8}}>
                {panelCreneaux.creneaux.map((c, i) => (
                  <button key={i} onClick={() => c.libre && selectCreneau(c)}
                    style={{padding:'10px 0', borderRadius:'var(--radius)',
                      border: c.libre ? '1px solid ' + couleur(panelCreneaux.plage.praticien_id).border : '0.5px solid var(--border)',
                      background: c.libre ? couleur(panelCreneaux.plage.praticien_id).bg : 'var(--bg)',
                      color: c.libre ? couleur(panelCreneaux.plage.praticien_id).text : 'var(--text-3)',
                      cursor: c.libre ? 'pointer' : 'default', fontSize:13, fontWeight: c.libre ? 500 : 400,
                      textDecoration: c.libre ? 'none' : 'line-through'}}
                    disabled={!c.libre}>
                    {minutesToTime(c.min)}
                  </button>
                ))}
              </div>
              {panelCreneaux.creneaux.every(c => !c.libre) && (
                <div className="empty" style={{marginTop:20}}>Aucun créneau disponible sur cette plage</div>
              )}
            </div>
          </div>
        </>
      )}

      {/* PANEL HORAIRES */}
      {panelHoraires && (
        <>
          <div className="panel-overlay" onClick={() => setPanelHoraires(false)} />
          <div className="panel">
            <div className="panel-header">
              <h3>Mes horaires de présence</h3>
              <button className="close-btn" onClick={() => setPanelHoraires(false)}>✕</button>
            </div>
            <div className="panel-body">
              <div style={{fontSize:13, fontWeight:500, marginBottom:10}}>Horaires habituels</div>
              <div className="form-row" style={{marginBottom:8}}>
                <div className="form-group">
                  <label>Jour</label>
                  <select value={horForm.jour_semaine} onChange={e => setHorForm(f => ({...f, jour_semaine: parseInt(e.target.value)}))}>
                    {JOURS.map((j, i) => <option key={i} value={i+1}>{j}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>De</label><input type="time" value={horForm.heure_debut} onChange={e => setHorForm(f => ({...f, heure_debut: e.target.value}))} /></div>
              </div>
              <div className="form-row" style={{marginBottom:12}}>
                <div className="form-group"><label>À</label><input type="time" value={horForm.heure_fin} onChange={e => setHorForm(f => ({...f, heure_fin: e.target.value}))} /></div>
                <div className="form-group" style={{justifyContent:'flex-end'}}>
                  <label>&nbsp;</label>
                  <button className="btn btn-primary btn-sm" onClick={saveHoraire}>+ Ajouter</button>
                </div>
              </div>
              {horairesHab.filter(h => h.praticien_id === praticien.id).map(h => (
                <div key={h.id} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 0', borderBottom:'0.5px solid var(--border)'}}>
                  <span style={{fontSize:13}}>{JOURS[h.jour_semaine-1]} · {h.heure_debut.slice(0,5)} – {h.heure_fin.slice(0,5)}</span>
                  <button className="btn btn-sm btn-danger" onClick={() => deleteHoraire(h.id)}>✕</button>
                </div>
              ))}
              <div style={{borderTop:'0.5px solid var(--border)', paddingTop:16, marginTop:16}}>
                <div style={{fontSize:13, fontWeight:500, marginBottom:10}}>Exception ponctuelle</div>
                <div className="form-row" style={{marginBottom:8}}>
                  <div className="form-group"><label>Date</label><input type="date" value={excForm.date_exception} onChange={e => setExcForm(f => ({...f, date_exception: e.target.value}))} /></div>
                  <div className="form-group">
                    <label>Type</label>
                    <select value={excForm.type} onChange={e => setExcForm(f => ({...f, type: e.target.value}))}>
                      <option value="absence">Absence</option>
                      <option value="presence">Présence exceptionnelle</option>
                    </select>
                  </div>
                </div>
                <div className="form-row" style={{marginBottom:8}}>
                  <div className="form-group"><label>De (vide = journée)</label><input type="time" value={excForm.heure_debut} onChange={e => setExcForm(f => ({...f, heure_debut: e.target.value}))} /></div>
                  <div className="form-group"><label>À</label><input type="time" value={excForm.heure_fin} onChange={e => setExcForm(f => ({...f, heure_fin: e.target.value}))} /></div>
                </div>
                <div className="form-group" style={{marginBottom:10}}>
                  <label>Motif (optionnel)</label>
                  <input value={excForm.motif} onChange={e => setExcForm(f => ({...f, motif: e.target.value}))} placeholder="Congé, formation…" />
                </div>
                <button className="btn btn-primary btn-sm" style={{width:'100%', justifyContent:'center'}} onClick={saveException} disabled={!excForm.date_exception}>
                  Enregistrer l'exception
                </button>
                {exceptions.filter(e => e.praticien_id === praticien.id).map(e => (
                  <div key={e.id} style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 0', borderBottom:'0.5px solid var(--border)', marginTop:8}}>
                    <span style={{fontSize:12}}>
                      {e.date_exception} · {e.type === 'absence' ? 'Absence' : 'Présence'}
                      {e.heure_debut ? ' ' + e.heure_debut.slice(0,5) + '–' + (e.heure_fin?.slice(0,5)||'') : ' (journée)'}
                      {e.motif ? ' · ' + e.motif : ''}
                    </span>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteException(e.id)}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
